import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { acceptInvitation, declineInvitation, fetchInvitations, revokeInvitation, sendInvitation } from './api'
import { StoreCompanyUserInput } from '../company/input/store-company-user-input'
import { CompanyRole } from '../company/company-role'
import { onErrorNotifyAboutValidationError } from '../util'

export function useInvitations(companyId: string | undefined, role: CompanyRole) {
    return useQuery({
        queryFn: () => fetchInvitations(companyId!, role),
        queryKey: ['company', 'invitations', companyId, role],
        enabled: !!companyId,
    })
}

export function useRevokeInvitation(companyId?: string, email?: string) {
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: () => revokeInvitation(companyId!, email!),
        onSuccess: () => {
            notification.success({ message: t('Invitation revoked') })
            queryClient.invalidateQueries({ queryKey: ['company', 'invitations', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useDeclineInvitation(companyId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => declineInvitation(companyId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['me'] })
        },
    })
}

export function useAcceptInvitation(companyId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => acceptInvitation(companyId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['me'] })
        },
    })
}

export function useSendInvitation(companyId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: StoreCompanyUserInput) => sendInvitation(companyId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', 'invitations', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}
